// @flow
import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import ResidentService from '../../../services/residentService';
import {
  LEASE_EXECUTED,
  LEASE_UPLOADED,
  LEASE_CANCELLED,
} from '../../LeaseDataTab/customEvents';

// Lease-signing happens on the Lease Data tab (a sibling of the card in the profile), which
// broadcasts these window events on execute / manual upload / cancel. The card listens for them
// to re-evaluate the "Lease has been executed" requirement on demand (cancel reverts to Pending).
const LEASE_CHANGE_EVENTS = [LEASE_EXECUTED, LEASE_UPLOADED, LEASE_CANCELLED];

/**
 * Returns the query key for the active transfer.
 */
export const activeTransferKey = (
  organizationId: string,
  propertyId: string,
  householdId: string,
) => ['activeTransfer', organizationId, propertyId, householdId];

/**
 * Fetches the active transfer for a household.
 * Returns the active UnitTransfer if one exists.
 */
export function useActiveTransfer(
  organizationId: string,
  propertyId: string,
  householdId: string,
  enabled: boolean,
) {
  const queryClient = useQueryClient();
  const key = activeTransferKey(organizationId, propertyId, householdId);

  // Refresh on demand when the transfer lease is executed, uploaded, or canceled on the Lease
  // Data tab, so the "Lease has been executed" requirement stays in sync without a full remount.
  useEffect(() => {
    if (!enabled) return undefined;
    const invalidate = () => queryClient.invalidateQueries(key);
    LEASE_CHANGE_EVENTS.forEach((evt) =>
      window.addEventListener(evt, invalidate),
    );
    return () =>
      LEASE_CHANGE_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, invalidate),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, organizationId, propertyId, householdId]);

  return useQuery(
    key,
    () => {
      const service = new ResidentService();
      return service.getActiveTransfer(organizationId, propertyId, householdId);
    },
    {
      // Requirement statuses are derived server-side from the current unit status, lease
      // execution state, and certification compliance — all changed on other screens. Fetch on
      // view (mount) and otherwise refresh on demand via invalidation from the mutations/events
      // that change those records. No refetch-on-focus (too aggressive here).
      enabled: Boolean(enabled && organizationId && propertyId && householdId),
      staleTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
    },
  );
}

/**
 * Returns a mutation function that updates transfer actions and refreshes the cache.
 */
export function useUpdateTransferActions({
  organizationId,
  propertyId,
  householdId,
}: {
  organizationId: string,
  propertyId: string,
  householdId: string,
}) {
  const queryClient = useQueryClient();
  return useMutation(
    (actions: Object) => {
      const service = new ResidentService();
      return service.updateTransferActions(
        organizationId,
        propertyId,
        householdId,
        actions,
      );
    },
    {
      onSuccess: (data) => {
        queryClient.setQueryData(
          activeTransferKey(organizationId, propertyId, householdId),
          data,
        );
      },
    },
  );
}

// Canceling an automated transfer unwinds several related records (transfer certs, the auto-created
// NTV on the active lease, the transfer lease, and the UnitTransfer status). Every React Query that
// reads any of those must be invalidated so the profile can't drift from the freshly unwound state.
// The cert keys mirror the profile's canonical set (ResidentProfile/index.js:1685-1687,:1858-1866).
export const cancelInvalidationKeys = [
  ['startTransferEligibility'],
  ['household', 'lease', 'validateTransferLease'],
  ['getHouseholdProfileAffordableQualificationsByProgram'],
  ['getQualificationHistory'],
  ['fetchHouseholdHasOpenCerts'],
  ['getCompletionStatusesAndAssistanceDetails'],
  ['getAffordableQualificationCompletionStatuses'],
  // Affordable-tab content also reads these; without them the tabs render pre-transfer
  // qualification/compliance/recert data even though the tab list re-derives from the
  // getOneResident reload. (ENG-30151)
  ['affordableQualifications'],
  ['getComplianceApprovalHistory'],
  ['fetchRecertDueDates'],
  ['affordableHouseholdDetails'],
  ['useCertificationsQuery'],
  ['documents'],
];

/**
 * Returns a mutation that cancels the active transfer and invalidates every query the unwind
 * affects (the active transfer itself plus certs, NTV/lease, and documents).
 */
export function useCancelActiveTransfer({
  organizationId,
  propertyId,
  householdId,
}: {
  organizationId: string,
  propertyId: string,
  householdId: string,
}) {
  const queryClient = useQueryClient();
  return useMutation(
    () => {
      const service = new ResidentService();
      return service.cancelActiveTransfer(
        organizationId,
        propertyId,
        householdId,
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          activeTransferKey(organizationId, propertyId, householdId),
        );
        cancelInvalidationKeys.forEach((key) =>
          queryClient.invalidateQueries(key),
        );
      },
    },
  );
}

/**
 * Returns a mutation that completes the active transfer, invalidating every query the move
 * affects (the active transfer plus certs, NTV/lease, and documents) and triggering a redux
 * reload of the profile so the moved household / new active lease render immediately.
 *
 * On a 409 confirm-time revalidation failure the transfer is still IN_PROGRESS, so the active
 * transfer query is re-invalidated to pull the refreshed requirement statuses back into the card.
 */
export function useCompleteActiveTransfer({
  organizationId,
  propertyId,
  householdId,
  onCompleteSuccess,
}: {
  organizationId: string,
  propertyId: string,
  householdId: string,
  onCompleteSuccess?: () => void,
}) {
  const queryClient = useQueryClient();
  return useMutation(
    () => {
      const service = new ResidentService();
      return service.completeActiveTransfer(
        organizationId,
        propertyId,
        householdId,
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          activeTransferKey(organizationId, propertyId, householdId),
        );
        cancelInvalidationKeys.forEach((key) =>
          queryClient.invalidateQueries(key),
        );
        if (onCompleteSuccess) onCompleteSuccess();
      },
      onError: () => {
        // A 409 (confirm-time revalidation failure) leaves the transfer IN_PROGRESS; re-pull the
        // active transfer so the card re-renders with the refreshed requirement statuses.
        queryClient.invalidateQueries(
          activeTransferKey(organizationId, propertyId, householdId),
        );
      },
    },
  );
}

/**
 * Returns a mutation that edits the active transfer and invalidates every query that reads
 * the transfer or related records (transfer itself plus certs, lease, and documents).
 */
export function useEditActiveTransfer({
  organizationId,
  propertyId,
  householdId,
}: {
  organizationId: string,
  propertyId: string,
  householdId: string,
}) {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: { transferReasonId?: string, transferDate?: string }) => {
      const service = new ResidentService();
      return service.editActiveTransfer(
        organizationId,
        propertyId,
        householdId,
        payload,
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          activeTransferKey(organizationId, propertyId, householdId),
        );
        cancelInvalidationKeys.forEach((key) =>
          queryClient.invalidateQueries(key),
        );
      },
    },
  );
}
